import React, { useEffect, useState } from "react";
import { Modal as EDSModal } from "@chanzuckerberg/eds";
import styled from "styled-components/macro";
import CloseButton, { CloseButtonProps } from "components/CloseButton";
import { theme } from "ui/theme";

const ModalContext = React.createContext({
  inModal: false,
  hasEntered: false,
});

export interface ModalProps
  extends Omit<React.ComponentProps<typeof EDSModal>, "onClose"> {
  /**
   * This must still be set, even without a close button, so that the modal can
   * be closed by clicking outside of it or by hitting the escape key.
   */
  close: CloseButtonProps;
  overrideSize?: "standard";
}

const $CloseWrapper = styled.div`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  // Ensure the close button is positioned above other absolutely positioned
  // elements in the modal.
  z-index: ${theme.zIndexLevelTwo};
`;

const $CloseButton = styled(CloseButton)`
  padding: 0.625rem 0.6875rem;
`;

const $ModalBody = styled(EDSModal.Body)`
  // This is needed to ensure that the modal body is scrollable when the modal
  // is open and the body is taller than the viewport.
  overflow-y: auto;
`;

const $Modal = styled(EDSModal)<{ $overridesize?: ModalProps["overrideSize"] }>`
  height: fit-content;
  ${(props) =>
    props.$overridesize === "standard" &&
    `max-width: ${theme.EdsStandardModal};`};
`;

function Modal({
  children,
  close,
  open,
  hideCloseButton,
  ...props
}: ModalProps) {
  const [hasEntered, setHasEntered] = useState(false);
  const { overrideSize, ...rest } = props;

  useEffect(() => {
    if (open) {
      setHasEntered(true);
    }
  }, [open]);

  return (
    <$Modal
      hideCloseButton
      onClose={close.onClick}
      open={open}
      $overridesize={overrideSize}
      {...rest}
    >
      {!hideCloseButton && (
        <$CloseWrapper>
          <$CloseButton {...close} />
        </$CloseWrapper>
      )}
      <ModalContext.Provider value={{ inModal: true, hasEntered }}>
        {children}
      </ModalContext.Provider>
    </$Modal>
  );
}

Modal.Header = EDSModal.Header;
Modal.Title = EDSModal.Title;
Modal.Body = $ModalBody;
Modal.Footer = EDSModal.Footer;
Modal.Stepper = EDSModal.Stepper;

export default Modal;
